import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type GtagCommand = 'config' | 'event' | 'set' | 'consent' | 'js';
type GtagArgs = [GtagCommand, string | Date, { [key: string]: unknown }?];
type DataLayerItem = GtagArgs | { [key: string]: unknown };

let has_initialized_google_analytics = false;
let last_tracked_page_path: string | null = null;
let last_tracked_page_timestamp = 0;

// Declare global Google Analytics and Tag Manager types
declare global {
  interface Window {
    dataLayer: DataLayerItem[];
    gtag: (...args: GtagArgs) => void;
  }
}

export const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID || import.meta.env.VITE_GA_TRACKING_ID;

function get_analytics_window() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window;
}

function get_gtag() {
  const analytics_window = get_analytics_window();
  if (!analytics_window) {
    return null;
  }

  analytics_window.dataLayer = analytics_window.dataLayer || [];

  if (!analytics_window.gtag) {
    analytics_window.gtag = (...args: GtagArgs) => {
      analytics_window.dataLayer.push(args);
    };
  }

  return analytics_window.gtag;
}

function initialize_google_analytics() {
  if (!GA_MEASUREMENT_ID || typeof document === 'undefined') {
    return null;
  }

  const gtag = get_gtag();
  if (!gtag) {
    return null;
  }

  if (!document.querySelector(`script[data-ga-measurement-id="${GA_MEASUREMENT_ID}"]`)) {
    const ga_script = document.createElement('script');
    ga_script.async = true;
    ga_script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    ga_script.dataset.gaMeasurementId = GA_MEASUREMENT_ID;
    document.head.appendChild(ga_script);
  }

  if (!has_initialized_google_analytics) {
    gtag('js', new Date());
    gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
    has_initialized_google_analytics = true;
  }

  return gtag;
}

function track_page_view(page_path: string) {
  const gtag = initialize_google_analytics();
  if (!gtag || typeof document === 'undefined') {
    return;
  }

  // Avoid spamming GA (and history.replaceState inside gtag.js) with
  // identical page_view events in a short window, which can trigger
  // Safari's "Attempt to use history.replaceState() more than 100 times
  // per 10 seconds" SecurityError.
  const now = Date.now();
  if (last_tracked_page_path === page_path && now - last_tracked_page_timestamp < 5_000) {
    return;
  }

  last_tracked_page_path = page_path;
  last_tracked_page_timestamp = now;

  gtag('event', 'page_view', {
    page_location: window.location.href,
    page_path,
    page_title: document.title,
    send_to: GA_MEASUREMENT_ID,
  });
}

// Custom hook to track page views
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    const page_path = `${location.pathname}${location.search}`;
    track_page_view(page_path);
  }, [location.pathname, location.search]);
};
